import * as React from 'react';
import { Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface Option<T extends string | number> {
  value: T;
  label: string;
  hint?: string;
}

interface QuestionProps {
  label: React.ReactNode;
  hint?: string;
  note?: string;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
}

export function Question({ label, hint, note, error, htmlFor, children }: QuestionProps) {
  const id = React.useId();
  return (
    <section className="space-y-3" aria-labelledby={htmlFor ? undefined : id} data-error={error ? true : undefined}>
      <div className="space-y-1">
        <Label id={id} htmlFor={htmlFor} className="text-base font-semibold leading-snug text-[#0A1628]">
          {label}
        </Label>
        {hint && <p className="text-sm text-slate-500">{hint}</p>}
      </div>
      {children}
      {note && <p className="text-sm text-slate-500">{note}</p>}
      {error && (
        <p role="alert" className="text-sm font-medium text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}

interface ChoiceProps<T extends string | number> {
  options: Option<T>[];
  value?: T;
  onChange: (value: T) => void;
  columns?: 2 | 3 | 4 | 7;
  label?: string;
}

const COLUMNS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  7: 'grid-cols-7',
};

/** Single-select group of large tap targets. */
export function Choice<T extends string | number>({ options, value, onChange, columns = 2, label }: ChoiceProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className={cn('grid gap-2', COLUMNS[columns])}>
      {options.map((o) => (
        <TapButton key={String(o.value)} role="radio" selected={value === o.value} onClick={() => onChange(o.value)} hint={o.hint}>
          {o.label}
        </TapButton>
      ))}
    </div>
  );
}

interface MultiChoiceProps<T extends string> {
  options: Option<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  label?: string;
  columns?: 2 | 4;
  showCheck?: boolean;
}

export function MultiChoice<T extends string>({ options, value, onChange, label, columns = 2, showCheck = true }: MultiChoiceProps<T>) {
  const toggle = (v: T) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div role="group" aria-label={label} className={cn('grid gap-2', COLUMNS[columns])}>
      {options.map((o) => (
        <TapButton key={o.value} role="checkbox" selected={value.includes(o.value)} onClick={() => toggle(o.value)} showCheck={showCheck}>
          {o.label}
        </TapButton>
      ))}
    </div>
  );
}

interface TapButtonProps {
  role: 'radio' | 'checkbox';
  selected: boolean;
  onClick: () => void;
  hint?: string;
  showCheck?: boolean;
  children: React.ReactNode;
}

function TapButton({ role, selected, onClick, hint, showCheck, children }: TapButtonProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-center text-[15px] font-semibold transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A5CFF] focus-visible:ring-offset-2 active:scale-[0.98]',
        selected
          ? 'border-[#0A5CFF] bg-[#0A5CFF]/[0.06] text-[#0A5CFF]'
          : 'border-slate-200 bg-white text-[#0A1628] hover:border-slate-300',
      )}
    >
      {showCheck && (
        <span
          className={cn(
            'grid h-5 w-5 shrink-0 place-content-center rounded-md border-2',
            selected ? 'border-[#0A5CFF] bg-[#0A5CFF] text-white' : 'border-slate-300',
          )}
        >
          {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
      )}
      <span className="flex flex-col">
        <span>{children}</span>
        {hint && <span className="text-xs font-medium text-slate-500">{hint}</span>}
      </span>
    </button>
  );
}

export const inputClass =
  'h-12 rounded-xl border-2 border-slate-200 bg-white text-base text-[#0A1628] focus-visible:border-[#0A5CFF] focus-visible:ring-0 focus-visible:ring-offset-0';
